import { contrastRatio, isHexColour, mixHex, relativeLuminance, rgbTriplet, } from "../schemes/contrast";
const SANS_FALLBACK = 'system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", sans-serif';
const SERIF_FALLBACK = 'Georgia, "Times New Roman", serif';
export const PORTAL_FONTS = {
    modern: {
        label: "Modern",
        note: "Inter Tight throughout. Neutral and contemporary.",
        display: `"Inter Tight", ${SANS_FALLBACK}`,
        body: `"Inter Tight", ${SANS_FALLBACK}`,
        displayWeight: 700,
        displayTracking: "-0.02em",
        displayScale: 1,
    },
    cinema: {
        label: "Cinema",
        note: "Instrument Serif headlines over DM Sans. A film-poster voice.",
        display: `"Instrument Serif", ${SERIF_FALLBACK}`,
        body: `"DM Sans", ${SANS_FALLBACK}`,
        displayWeight: 400,
        displayTracking: "0",
        displayScale: 1.14,
    },
    classic: {
        label: "Classic",
        note: "Cormorant Garamond over Manrope. Literary, arthouse.",
        display: `"Cormorant Garamond", ${SERIF_FALLBACK}`,
        body: `Manrope, ${SANS_FALLBACK}`,
        displayWeight: 600,
        displayTracking: "0",
        displayScale: 1.12,
    },
    editorial: {
        label: "Editorial",
        note: "Newsreader over Work Sans. A magazine's calm.",
        display: `Newsreader, ${SERIF_FALLBACK}`,
        body: `"Work Sans", ${SANS_FALLBACK}`,
        displayWeight: 600,
        displayTracking: "-0.01em",
        displayScale: 1.04,
    },
    graphic: {
        label: "Graphic",
        note: "Space Grotesk over Onest. Bold, festival-programme energy.",
        display: `"Space Grotesk", ${SANS_FALLBACK}`,
        body: `Onest, ${SANS_FALLBACK}`,
        displayWeight: 700,
        displayTracking: "-0.025em",
        displayScale: 1,
    },
    friendly: {
        label: "Friendly",
        note: "Figtree throughout. Round and warm, good for family catalogues.",
        display: `Figtree, ${SANS_FALLBACK}`,
        body: `Figtree, ${SANS_FALLBACK}`,
        displayWeight: 800,
        displayTracking: "-0.015em",
        displayScale: 1,
    },
};
export const PORTAL_FONT_KEYS = Object.keys(PORTAL_FONTS);
export function isPortalFontKey(v) {
    return (typeof v === "string" &&
        Object.prototype.hasOwnProperty.call(PORTAL_FONTS, v));
}
export const PORTAL_CORNERS = ["rounded", "sharp"];
export const PORTAL_DENSITIES = ["comfortable", "compact"];
export const APPEARANCE_LIMITS = {
    heroHeadline: 80,
    heroSubline: 220,
    tagline: 90,
    url: 500,
    featuredOfferId: 100,
};
export function isPortalAssetUrl(v) {
    if (typeof v !== "string")
        return false;
    if (v.length > APPEARANCE_LIMITS.url)
        return false;
    const [path, query, ...rest] = v.split("?");
    if (rest.length > 0 || path === undefined)
        return false;
    if (/\.\.|\/\/|\\|%2e%2e|%2f|%5c/i.test(path))
        return false;
    if (!/^[A-Za-z0-9/_.\-~%+@,=]+$/.test(path))
        return false;
    if (path.startsWith("/foyer-assets/") && path.length > 14) {
        return query === undefined;
    }
    const scalerPrefix = [
        "/api/media/v1/imagescaler/",
        "/api/media/v1/imagescaler/",
    ].find((prefix) => path.startsWith(prefix));
    if (scalerPrefix && path.length > scalerPrefix.length) {
        return query === undefined || /^[A-Za-z0-9=&._-]{1,200}$/.test(query);
    }
    return false;
}
const blank = (v) => v === undefined || v === null || v === "";
function cleanLine(input, field, max) {
    if (blank(input))
        return { ok: true, value: undefined };
    if (typeof input !== "object" || Array.isArray(input)) {
        return { ok: false, field, reason: "sv_en_object" };
    }
    const out = {};
    for (const [k, v] of Object.entries(input)) {
        if (k !== "sv" && k !== "en") {
            return { ok: false, field: `${field}.${k}`, reason: "sv_or_en" };
        }
        if (blank(v))
            continue;
        if (typeof v !== "string") {
            return { ok: false, field: `${field}.${k}`, reason: "not_text" };
        }
        const text = v.replace(/\s+/g, " ").trim();
        if (/[\u0000-\u001f\u007f]/.test(text)) {
            return { ok: false, field: `${field}.${k}`, reason: "not_text" };
        }
        if (Array.from(text).length > max) {
            return { ok: false, field: `${field}.${k}`, reason: `max_${max}` };
        }
        if (text)
            out[k] = text;
    }
    return { ok: true, value: Object.keys(out).length ? out : undefined };
}
export function cleanPortalAppearance(input) {
    if (blank(input))
        return { ok: true, value: {} };
    if (typeof input !== "object" || Array.isArray(input)) {
        return { ok: false, field: "appearance", reason: "not_an_object" };
    }
    const a = input;
    const known = new Set([
        "heroImageUrl",
        "heroHeadline",
        "heroSubline",
        "tagline",
        "font",
        "corners",
        "density",
        "faviconUrl",
        "featuredOfferId",
    ]);
    for (const k of Object.keys(a)) {
        if (!known.has(k)) {
            return { ok: false, field: `appearance.${k}`, reason: "unknown_field" };
        }
    }
    const out = {};
    for (const k of ["heroImageUrl", "faviconUrl"]) {
        if (blank(a[k]))
            continue;
        const v = typeof a[k] === "string" ? a[k].trim() : a[k];
        if (!isPortalAssetUrl(v)) {
            return { ok: false, field: `appearance.${k}`, reason: "own_asset_url" };
        }
        out[k] = v;
    }
    const lines = [
        ["heroHeadline", APPEARANCE_LIMITS.heroHeadline],
        ["heroSubline", APPEARANCE_LIMITS.heroSubline],
        ["tagline", APPEARANCE_LIMITS.tagline],
    ];
    for (const [k, max] of lines) {
        const r = cleanLine(a[k], `appearance.${k}`, max);
        if (!r.ok)
            return r;
        if (r.value)
            out[k] = r.value;
    }
    if (!blank(a.font)) {
        if (!isPortalFontKey(a.font)) {
            return { ok: false, field: "appearance.font", reason: "unknown_font" };
        }
        out.font = a.font;
    }
    if (!blank(a.corners)) {
        if (!PORTAL_CORNERS.includes(a.corners)) {
            return {
                ok: false,
                field: "appearance.corners",
                reason: "rounded_or_sharp",
            };
        }
        out.corners = a.corners;
    }
    if (!blank(a.density)) {
        if (!PORTAL_DENSITIES.includes(a.density)) {
            return {
                ok: false,
                field: "appearance.density",
                reason: "comfortable_or_compact",
            };
        }
        out.density = a.density;
    }
    if (!blank(a.featuredOfferId)) {
        const v = a.featuredOfferId;
        if (typeof v !== "string" ||
            v.length > APPEARANCE_LIMITS.featuredOfferId ||
            !/^[A-Za-z0-9_-]+$/.test(v)) {
            return {
                ok: false,
                field: "appearance.featuredOfferId",
                reason: "an_id",
            };
        }
        out.featuredOfferId = v;
    }
    return { ok: true, value: out };
}
export function cleanPortalBrand(input, currentLogoUrl) {
    if (blank(input))
        return { ok: true, value: {} };
    if (typeof input !== "object" || Array.isArray(input)) {
        return { ok: false, field: "brand", reason: "not_an_object" };
    }
    const b = input;
    const out = {};
    for (const k of Object.keys(b)) {
        if (!["scheme", "bg", "ink", "accent", "logoUrl"].includes(k)) {
            return { ok: false, field: `brand.${k}`, reason: "unknown_field" };
        }
    }
    if (!blank(b.scheme)) {
        if (typeof b.scheme !== "string" || b.scheme.length > 40) {
            return { ok: false, field: "brand.scheme", reason: "a_template_key" };
        }
        out.scheme = b.scheme;
    }
    for (const k of ["bg", "ink", "accent"]) {
        if (blank(b[k]))
            continue;
        const v = typeof b[k] === "string" ? b[k].trim() : b[k];
        if (!isHexColour(v)) {
            return { ok: false, field: `brand.${k}`, reason: "hex_colour" };
        }
        out[k] = v;
    }
    if (!blank(b.logoUrl)) {
        const v = typeof b.logoUrl === "string" ? b.logoUrl.trim() : b.logoUrl;
        if (!(v === currentLogoUrl && typeof v === "string") &&
            !isPortalAssetUrl(v)) {
            return { ok: false, field: "brand.logoUrl", reason: "own_asset_url" };
        }
        out.logoUrl = v;
    }
    return { ok: true, value: out };
}
export function portalLine(line, lang) {
    var _a, _b;
    if (!line)
        return "";
    return ((_a = line[lang]) === null || _a === void 0 ? void 0 : _a.trim()) || ((_b = line[lang === "sv" ? "en" : "sv"]) === null || _b === void 0 ? void 0 : _b.trim()) || "";
}
export const TEXT_CONTRAST = 4.5;
export const UI_CONTRAST = 3;
export function readableAccent(roles) {
    const grounds = [roles.paper, roles.surface];
    const reads = (c) => grounds.every((g) => { var _a; return ((_a = contrastRatio(c, g)) !== null && _a !== void 0 ? _a : 0) >= TEXT_CONTRAST; });
    if (reads(roles.accent))
        return roles.accent;
    for (const t of [0.25, 0.45, 0.65, 0.8]) {
        const c = mixHex(roles.accent, roles.ink, t);
        if (c && reads(c))
            return c;
    }
    return roles.ink;
}
export function focusColour(roles) {
    var _a;
    return ((_a = contrastRatio(roles.accent, roles.paper)) !== null && _a !== void 0 ? _a : 0) >= UI_CONTRAST
        ? roles.accent
        : roles.ink;
}
export function portalSkin(roles, appearance) {
    const vars = {};
    if (roles) {
        const set = (name, v) => {
            if (typeof v === "string" && v.trim())
                vars[name] = v;
        };
        set("--paper", roles.paper);
        set("--surface", roles.surface);
        set("--surface-alt", roles.surfaceAlt);
        set("--ink", roles.ink);
        set("--ink-soft", roles.inkSoft);
        set("--ink-muted", roles.inkMuted);
        set("--hairline", roles.hairline);
        set("--accent", roles.accent);
        set("--accent-deep", roles.accentDeep);
        set("--accent-tint", roles.accentTint);
        set("--on-accent", roles.onAccent);
        set("--danger", roles.danger);
        set("--on-danger", roles.onDanger);
        set("--sans", roles.sans);
        set("--display", roles.display);
        set("--mono", roles.mono);
        set("--accent-ink", readableAccent(roles));
        set("--focus", focusColour(roles));
        set("--paper-rgb", rgbTriplet(roles.paper));
        set("--ink-rgb", rgbTriplet(roles.ink));
        set("--accent-rgb", rgbTriplet(roles.accent));
    }
    const font = (appearance === null || appearance === void 0 ? void 0 : appearance.font) ? PORTAL_FONTS[appearance.font] : null;
    if (font) {
        vars["--sans"] = font.body;
        vars["--display"] = font.display;
        vars["--display-weight"] = String(font.displayWeight);
        vars["--display-tracking"] = font.displayTracking;
        vars["--display-scale"] = String(font.displayScale);
    }
    if ((appearance === null || appearance === void 0 ? void 0 : appearance.corners) === "sharp") {
        vars["--radius"] = "3px";
        vars["--radius-sm"] = "2px";
        vars["--radius-lg"] = "4px";
        vars["--radius-pill"] = "3px";
    }
    if ((appearance === null || appearance === void 0 ? void 0 : appearance.density) === "compact") {
        vars["--card-w"] = "228px";
        vars["--grid-min"] = "180px";
        vars["--gap"] = "12px";
    }
    const lum = roles ? relativeLuminance(roles.paper) : null;
    return { vars, colorScheme: lum !== null && lum < 0.25 ? "dark" : "light" };
}
export function explainPortalColours(brand, roles) {
    var _a, _b;
    const r = (a, b) => contrastRatio(a, b);
    const fmt = (n) => n === null ? "?" : `${(Math.floor(n * 10) / 10).toFixed(1)}:1`;
    const checks = [];
    const textRatio = r(roles.ink, roles.paper);
    const text = {
        id: "text",
        label: "Body text on the background",
        ratio: textRatio,
        required: TEXT_CONTRAST,
        pass: (textRatio !== null && textRatio !== void 0 ? textRatio : 0) >= TEXT_CONTRAST,
    };
    const wantedInk = (_a = brand === null || brand === void 0 ? void 0 : brand.ink) === null || _a === void 0 ? void 0 : _a.trim();
    if (wantedInk &&
        ((_b = roles.ignored) === null || _b === void 0 ? void 0 : _b.some((i) => i.field === "ink")) &&
        wantedInk.toLowerCase() !== roles.ink.toLowerCase()) {
        text.adjusted = {
            wanted: wantedInk,
            used: roles.ink,
            why: `${wantedInk} on ${roles.paper} is only ${fmt(r(wantedInk, roles.paper))}; body text needs ${TEXT_CONTRAST}:1, so the portal writes in ${roles.ink} instead.`,
        };
    }
    checks.push(text);
    const accentInk = readableAccent(roles);
    const linkRatio = r(accentInk, roles.paper);
    const links = {
        id: "links",
        label: "Links and prices in the accent",
        ratio: linkRatio,
        required: TEXT_CONTRAST,
        pass: (linkRatio !== null && linkRatio !== void 0 ? linkRatio : 0) >= TEXT_CONTRAST,
    };
    if (accentInk.toLowerCase() !== roles.accent.toLowerCase()) {
        links.adjusted = {
            wanted: roles.accent,
            used: accentInk,
            why: `The accent reads ${fmt(r(roles.accent, roles.paper))} on the background: fine for buttons, too faint for small text. Links and prices use the deeper ${accentInk}.`,
        };
    }
    checks.push(links);
    const buttonRatio = r(roles.onAccent, roles.accent);
    checks.push({
        id: "buttons",
        label: `Button labels (${roles.onAccent.toLowerCase() === "#ffffff" || roles.onAccent.toLowerCase() === "#fff" ? "white" : "dark"}, picked for the accent)`,
        ratio: buttonRatio,
        required: TEXT_CONTRAST,
        pass: (buttonRatio !== null && buttonRatio !== void 0 ? buttonRatio : 0) >= TEXT_CONTRAST,
        ...((buttonRatio !== null && buttonRatio !== void 0 ? buttonRatio : 0) < TEXT_CONTRAST
            ? {
                adjusted: {
                    wanted: roles.accent,
                    used: roles.accent,
                    why: `Neither white nor black reaches ${TEXT_CONTRAST}:1 on this mid-tone accent. Pick a darker or a lighter accent for readable buttons.`,
                },
            }
            : {}),
    });
    const focus = focusColour(roles);
    const focusRatio = r(focus, roles.paper);
    checks.push({
        id: "focus",
        label: "Keyboard focus ring",
        ratio: focusRatio,
        required: UI_CONTRAST,
        pass: (focusRatio !== null && focusRatio !== void 0 ? focusRatio : 0) >= UI_CONTRAST,
        ...(focus !== roles.accent
            ? {
                adjusted: {
                    wanted: roles.accent,
                    used: focus,
                    why: `The accent is under ${UI_CONTRAST}:1 on the background, so a focus ring in it would be invisible; it is drawn in the text colour.`,
                },
            }
            : {}),
    });
    return checks;
}
